import FilterMenu from '../FilterMenu'
import { RequestProps, Technology } from '../../../store/types'
import React, { useState } from 'react'
import CollapsibleSection from '../../Button/CollapsibleSection'
import { t } from 'i18next'
import { Autocomplete, Checkbox, FormControlLabel, ListItem } from '@mui/material'
import TechnologyLogo from '../../Logo/TechnologyLogo'
import TextField from '@mui/material/TextField'
import { useGetTechnologiesQuery } from '../../../store/redux/reducers/technologiesSlice'
import { isEqual } from 'lodash'
import { useGetDictionariesQuery, useGetNestedDictionaryQuery } from '../../../store/redux/reducers/dictionarySlice'
import { useLeftPadding } from '../../../store/redux/hooks'

type FilterCandidatesMenuProps = {
  requestProps: RequestProps
  setRequestProps: React.Dispatch<React.SetStateAction<RequestProps>>
}

const FilterCandidatesMenu = ({ requestProps, setRequestProps }: FilterCandidatesMenuProps) => {
  const leftPadding = useLeftPadding()
  const { data: technologies } = useGetTechnologiesQuery({ page: 0, size: 500 })
  const [selectedTechnologies, setSelectedTechnologies] = useState<Technology[]>([])
  const [chosenStage, setChosenStage] = useState<string | undefined>(undefined)
  const [steps, setSteps] = useState<string[]>([])
  const [localization, setLocalization] = useState<string | undefined>(undefined)
  const { data: stagesResponse } = useGetDictionariesQuery({
    page: 0,
    size: 20,
    group: 'RECRUITMENT_WORKFLOW',
    sortField: 'order',
  })
  const { data: nestedResponse } = useGetNestedDictionaryQuery('RECRUITMENT_WORKFLOW')
  const { data: localizationResponse } = useGetDictionariesQuery({
    page: 0,
    size: 20,
    group: 'localization',
    sortField: 'value',
  })

  const handleStageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value as string
    let isChecked = e.target.checked

    if (isChecked) {
      if (value !== chosenStage) {
        setSteps([])
      }
      setChosenStage(value)
    } else {
      setChosenStage(undefined)
    }
  }

  const handleStepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value as string
    let isChecked = e.target.checked

    if (isChecked) {
      setSteps([...steps, value])
    } else {
      setSteps(steps.filter(e => e !== value))
    }
  }

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value as string
    let isChecked = e.target.checked

    if (isChecked) {
      setLocalization(value)
    } else {
      setLocalization(undefined)
    }
  }

  const handleFilter = () => {
    let newProps: RequestProps = { ...requestProps, page: 0 }

    newProps = {
      ...newProps,
      'technologies.technologyDefinition.id':
        selectedTechnologies.length !== 0
          ? selectedTechnologies.map(technology => technology.id).toString()
          : undefined,
      'recruitments.stage.id': chosenStage ?? undefined,
      'recruitments.step.id': steps.length !== 0 ? steps.toString() : undefined,
      'localization.id': localization ?? undefined,
    }

    if (isEqual(requestProps, newProps)) return
    setRequestProps({ ...newProps })
  }

  const handleClear = () => {
    setChosenStage(undefined)
    setSteps([])
    setSelectedTechnologies([])
    setLocalization(undefined)
    setRequestProps({
      page: 0,
      size: requestProps.size,
    })
  }

  return (
    <FilterMenu clear={handleClear} filter={handleFilter}>
      <CollapsibleSection text={t('page.candidates.table.technologies')}>
        <Autocomplete
          sx={{ pl: leftPadding }}
          multiple
          id="technologies-autocomplete"
          size="small"
          disableCloseOnSelect
          limitTags={3}
          options={technologies ? technologies.content : []}
          value={selectedTechnologies}
          onChange={(_event, value) => {
            setSelectedTechnologies(value)
          }}
          getOptionLabel={option => option.name}
          renderOption={(props, option, { selected }) => (
            <ListItem {...props} sx={{ gap: 1 }} key={option.id}>
              <Checkbox sx={{ p: 0.5 }} checked={selected} />
              <TechnologyLogo size={24} technology={option} />
              {option.name}
            </ListItem>
          )}
          renderInput={params => (
            <TextField
              size="small"
              fullWidth
              inputProps={{ ...params.inputProps, 'aria-label': t('page.users.filtering.technologies') }}
              InputProps={params.InputProps}
              label={t('page.users.filtering.technologies')}
            />
          )}
        />
      </CollapsibleSection>
      <CollapsibleSection text={t('page.candidates.table.stage')}>
        {stagesResponse &&
          stagesResponse.content.map(stage => (
            <React.Fragment key={stage.id}>
              <FormControlLabel
                sx={{ pl: leftPadding }}
                control={<Checkbox checked={chosenStage === stage.id} onChange={handleStageChange} />}
                label={stage.value}
                value={stage.id}
              />
              {chosenStage === stage.id &&
                Object.values(nestedResponse?.nestedDictionary[stage.key] || {}).map(step => (
                  <FormControlLabel
                    sx={{ pl: leftPadding, ml: 1 }}
                    key={step.id}
                    control={<Checkbox checked={steps.includes(step.id)} onChange={handleStepChange} />}
                    label={step.value}
                    value={step.id}
                  />
                ))}
            </React.Fragment>
          ))}
      </CollapsibleSection>
      <CollapsibleSection text={t('page.candidates.table.localization')}>
        {localizationResponse &&
          localizationResponse.content.map(local => (
            <FormControlLabel
              key={local.id}
              sx={{ pl: leftPadding }}
              control={<Checkbox checked={localization === local.id} onChange={handleLocalChange} />}
              label={local.value}
              value={local.id}
            />
          ))}
      </CollapsibleSection>
    </FilterMenu>
  )
}

export default FilterCandidatesMenu